// PropertyCarousel — visual, tactile alternative to the "select a
// property" dropdown inside HomeBookingWidget. Fully synced with the
// same `selectedSlugs` state so ticking a card here appears as ticked
// in the dropdown/checkbox list AND generates a new editable block
// below. Untoggling from any surface flows back here.
//
// Layout: horizontal scroll on all viewports with snap on mobile so
// each card lands cleanly. Cards adapt to viewport (~62vw on mobile,
// ~34vw on tablet, ~15rem on desktop). Selected state uses a strong
// border + orange overlay tick to keep the choice unmistakable.

import { useMemo, useRef } from "react";
import { ChevronLeft, ChevronRight, Check, MapPin, Pencil } from "lucide-react";
import PropertyThumb from "@/components/PropertyThumb";
import PropertyCardMiniMap from "@/components/PropertyCardMiniMap";
import { HOTELS } from "@/lib/data";
import { getCampsForHotel, formatCampShortList } from "@/lib/bookingCamps";

const pick = (obj, lang) => (obj?.[lang] ?? obj?.en ?? "");

// Location may be a plain string ("Arfoud") or an i18n object
// ({en:"Arfoud", fr:"Arfoud", es:"Arfoud"}). Normalize gracefully.
const pickLocation = (loc, lang) => {
  if (!loc) return "";
  if (typeof loc === "string") return loc;
  return pick(loc, lang);
};

const COPY = {
  overline: {
    en: "Browse the properties",
    fr: "Parcourir les adresses",
    es: "Explorar los alojamientos",
  },
  helper: {
    en: "Tap a card to add it to your enquiry — pick as many as you'd like.",
    fr: "Touchez une carte pour l'ajouter à votre demande — autant que vous le souhaitez.",
    es: "Toca una tarjeta para añadirla a tu solicitud — cuantas quieras.",
  },
  scroll_left: { en: "Scroll left", fr: "Défiler à gauche", es: "Desplazar a la izquierda" },
  scroll_right: { en: "Scroll right", fr: "Défiler à droite", es: "Desplazar a la derecha" },
  selected: { en: "Selected", fr: "Sélectionné", es: "Seleccionado" },
  count_hotel: { en: "hotels", fr: "hôtels", es: "hoteles" },
  count_bivouac: { en: "bivouacs", fr: "bivouacs", es: "vivacs" },
  count_refuge: { en: "refuges", fr: "refuges", es: "refugios" },
};

// Category → display label ("Hotel", "Bivouac", "Mountain refuge") and
// dot color. Falls back to "Property" for unexpected values so the
// carousel never breaks if new categories land in the data.
const CATEGORY_META = {
  hotel: {
    label: { en: "Hotel", fr: "Hôtel", es: "Hotel" },
    dot: "bg-[#C16542]",
  },
  bivouac: {
    label: { en: "Bivouac · Desert", fr: "Bivouac · Désert", es: "Vivac · Desierto" },
    dot: "bg-[#D4A373]",
  },
  refuge: {
    label: { en: "Mountain refuge", fr: "Refuge de montagne", es: "Refugio de montaña" },
    dot: "bg-[#5C5248]",
  },
};

const CATEGORY_ORDER = ["hotel", "bivouac", "refuge"];

export default function PropertyCarousel({
  blocks = [],
  selectedSlugs = [],
  onToggle,
  onEditCamp,
  onOpenLocationModal,
  lang,
  properties: propertiesProp,
}) {
  const properties = useMemo(
    () => propertiesProp || HOTELS.filter((h) => !h.coming_soon && h.bookable !== false),
    [propertiesProp]
  );
  const scrollRef = useRef(null);

  // Group + count by category for the header pill (informational only).
  const countsByCategory = useMemo(() => {
    const map = { hotel: 0, bivouac: 0, refuge: 0 };
    properties.forEach((h) => {
      if (map[h.category] != null) map[h.category] += 1;
    });
    return map;
  }, [properties]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(240, Math.round(el.clientWidth * 0.7));
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <div
      className="mt-5 border border-[#D4A373]/40 bg-[#FDF8EE]/60"
      data-testid="home-booking-property-carousel"
      data-no-cms-text="true"
    >
      {/* Header */}
      <div className="px-4 md:px-5 pt-4 pb-3 flex items-start md:items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
            {pick(COPY.overline, lang)}
          </p>
          <p className="text-[12px] text-[#5C5248] leading-snug mt-1 max-w-md">
            {pick(COPY.helper, lang)}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {/* Category counts — informational */}
          <div className="hidden sm:flex items-center gap-3 text-[10px] tracking-[0.22em] uppercase text-[#5C5248]">
            {CATEGORY_ORDER.map((cat) => (
              countsByCategory[cat] > 0 ? (
                <span key={cat} className="inline-flex items-center gap-1.5">
                  <span className={`inline-block w-1.5 h-1.5 rounded-full ${CATEGORY_META[cat].dot}`} aria-hidden="true" />
                  {countsByCategory[cat]} {pick(COPY[`count_${cat}`], lang)}
                </span>
              ) : null
            ))}
          </div>
          {/* Scroll arrows — desktop only, non-blocking on touch */}
          <div className="hidden md:flex items-center gap-1">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label={pick(COPY.scroll_left, lang)}
              className="w-8 h-8 inline-flex items-center justify-center bg-white border border-[#2C2621]/15 hover:border-[#C16542] text-[#5C5248] hover:text-[#C16542] transition-colors"
              data-testid="home-booking-property-carousel-scroll-left"
            >
              <ChevronLeft className="w-4 h-4" strokeWidth={1.6} />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label={pick(COPY.scroll_right, lang)}
              className="w-8 h-8 inline-flex items-center justify-center bg-white border border-[#2C2621]/15 hover:border-[#C16542] text-[#5C5248] hover:text-[#C16542] transition-colors"
              data-testid="home-booking-property-carousel-scroll-right"
            >
              <ChevronRight className="w-4 h-4" strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </div>

      {/* Card rail */}
      <ul
        ref={scrollRef}
        className="flex gap-3 md:gap-4 px-4 md:px-5 pb-4 md:pb-5 overflow-x-auto snap-x snap-mandatory md:snap-none [scrollbar-width:thin] [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-thumb]:bg-[#D4A373]/60"
        data-testid="home-booking-property-carousel-rail"
      >
        {properties.map((h) => {
          const selected = selectedSlugs.includes(h.slug);
          const camps = getCampsForHotel(h.slug);
          const chosenCampSlugs = blocks
            .filter((b) => b.slug === h.slug && b.campSlug)
            .map((b) => b.campSlug);
          const chosenCampShortList = chosenCampSlugs.length > 0
            ? formatCampShortList(h.slug, chosenCampSlugs, lang)
            : "";
          const meta = CATEGORY_META[h.category] || {
            label: { en: "Property", fr: "Adresse", es: "Propiedad" },
            dot: "bg-[#5C5248]",
          };
          return (
            <li
              key={h.slug}
              className="snap-start shrink-0 w-[62vw] xs:w-[46vw] sm:w-[34vw] md:w-[15rem] lg:w-[16rem]"
            >
              <button
                type="button"
                onClick={() => onToggle?.(h.slug)}
                aria-pressed={selected}
                data-testid={`home-booking-property-card-${h.slug}`}
                data-selected={selected ? "true" : "false"}
                className={`group relative w-full text-left bg-white border transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C16542] ${
                  selected
                    ? "border-[#C16542] shadow-[0_0_0_1px_#C16542]"
                    : "border-[#2C2621]/12 hover:border-[#C16542]/60"
                }`}
              >
                <div className="relative">
                  <PropertyThumb
                    slug={h.slug}
                    alt={h.name}
                    aspectRatio="4/3"
                    className="w-full"
                    testId={`home-booking-property-card-${h.slug}-thumb`}
                    cmsSlot={`booking-widget.thumb.${h.slug}`}
                  />
                  {/* Category chip — bottom-left overlay */}
                  <span className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 px-2 py-1 bg-white/95 text-[9px] tracking-[0.2em] uppercase text-[#2C2621]">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`} aria-hidden="true" />
                    {pick(meta.label, lang)}
                  </span>
                  {/* Selected badge — top-left overlay. Kept away from the
                      bottom-right corner where the Xaluca X monogram
                      (via `.x-watermark-scope ::after` in index.css)
                      occupies ~58% of the card width, so the badge
                      never fights the brand mark for legibility. */}
                  {selected ? (
                    <span
                      className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 bg-[#C16542] text-white text-[9px] tracking-[0.22em] uppercase shadow-md"
                      data-testid={`home-booking-property-card-${h.slug}-selected-badge`}
                    >
                      <Check className="w-3 h-3" strokeWidth={2.2} />
                      {pick(COPY.selected, lang)}
                    </span>
                  ) : null}
                </div>
                <div className="p-3">
                  <p className="text-[13px] leading-snug text-[#2C2621] font-serif-x break-words">
                    {h.name}
                  </p>
                  {(() => {
                    const loc = pickLocation(h.location, lang);
                    const region = pickLocation(
                      lang === "fr" && h.region_fr ? h.region_fr
                      : lang === "es" && h.region_es ? h.region_es
                      : h.region,
                      lang,
                    );
                    if (!loc && !region) return null;
                    return (
                      <p
                        className="mt-1.5 inline-flex items-start gap-1.5 text-[11px] text-[#5C5248] leading-snug break-words"
                        data-testid={`home-booking-property-card-${h.slug}-location`}
                      >
                        <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#A07042]" strokeWidth={1.7} />
                        <span>
                          {loc || region}
                          {loc && region ? (
                            <span className="text-[#5C5248]/60">
                              {" · "}{region}
                            </span>
                          ) : null}
                        </span>
                      </p>
                    );
                  })()}
                  {/* Landscape satellite mini-map — 4:3 aspect gives
                      the map a horizontal frame that pairs neatly
                      with the 4:3 photo above and reads as a natural
                      "location card" companion. Edge-to-edge via
                      negative horizontal + bottom margins so it
                      flows into the card chrome. Non-interactive
                      (pointer-events-none) on the tile layer — clicks
                      fall through to the outer <button> so tapping
                      the map SELECTS the accommodation. The embedded
                      "OPEN ROUTE" pill opens the enlarged location
                      modal (stopPropagation so tapping it doesn't
                      also toggle-select the card). The modal itself
                      renders the same mini-map component but WITHOUT
                      the `onOpenModal` prop, so its pill acts as the
                      final Google-Maps handoff. Lazy-loaded via
                      IntersectionObserver so off-screen carousel
                      cards don't pay the Leaflet init cost. */}
                  <div
                    className="mt-2.5 -mx-3 -mb-3"
                    data-testid={`home-booking-property-card-${h.slug}-map-wrap`}
                  >
                    <PropertyCardMiniMap
                      hotelSlug={h.slug}
                      hotelName={h.name}
                      hotelLocation={pickLocation(h.location, lang)}
                      hotelCategory={h.category}
                      aspectRatio="4/3"
                      className="border-l-0 border-r-0 border-b-0"
                      onOpenModal={() => onOpenLocationModal?.(h)}
                    />
                  </div>
                  {/* Selected-camps summary for parent-category
                      properties. Rendered inside the selected card so
                      the choice is visible at a glance; the "Change"
                      button reopens the second-step modal. Clicks
                      stopPropagation so tapping the summary doesn't
                      also untoggle the parent card. */}
                  {selected && camps.length > 0 && chosenCampShortList ? (
                    <div
                      className="mt-3 flex items-center justify-between gap-2 border-t border-[#2C2621]/10 pt-2"
                      data-testid={`home-booking-property-card-${h.slug}-camp-summary`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <p className="text-[11px] text-[#2C2621] leading-tight truncate">
                        <span className="text-[#A07042] mr-1">·</span>
                        {chosenCampShortList}
                      </p>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditCamp?.(h.slug);
                        }}
                        data-testid={`home-booking-property-card-${h.slug}-edit-camp`}
                        className="inline-flex items-center gap-1 text-[10px] tracking-[0.16em] uppercase text-[#C16542] hover:text-[#a04d2f] transition-colors shrink-0"
                      >
                        <Pencil className="w-3 h-3" strokeWidth={1.8} />
                        {pick({ en: "Change", fr: "Modifier", es: "Cambiar" }, lang)}
                      </button>
                    </div>
                  ) : null}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
